import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyValue } from '@/main/models/common';
import { useForm } from 'react-hook-form';
import * as S from './styles';
import {
  UserDetailsRequest,
  UserSaveRequest
} from '@/main/models/request/userRequest';
import { toast } from 'react-toastify';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import {
  getCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import {
  getCompany,
  resetReducerCompany,
  useCompanyReducer
} from '@/main/store/company';
import { getCulture, useCultureReducer } from '@/main/store/culture';
import { getTimeZonesUser, useTimeZoneReducer } from '@/main/store/timeZone';
import {
  getUsersGroupUser,
  resetReducerUsersGroupUser,
  useUserGroupReducer
} from '@/main/store/userGroup';
import { useDispatch } from 'react-redux';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { countryInfoApi, userApi } from '@/main/api';
import {
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isSubDomainQualitas
} from '@/main/services/utils';
import { userIsAdmin } from '@/main/services/utilsUser';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import {
  CompanyFilterRequest,
  UserGroupFilterRequest
} from '@/main/models/request';
import {
  getCompanyBranch,
  useCompanyBranchReducer,
  resetReducerCompanyBranch
} from '@/main/store/companyBranch';
import { StatusUserEnum } from '@/main/services/enum/StatusUserEnum';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import { getOptionsStatus } from '@/main/services/data/data';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import Button from '@/main/components/shared/button/button';
import { useUserReducer } from '@/main/store/user';
import { StatusCompanyEnum } from '@/main/services/enum/StatusCompanyEnum';
import { getUserType, useUserTypeReducer } from '@/main/store/userType';
import { UserTypeEnum } from '@/main/services/enum/UserTypeEnum';
import CopyToClipboardText from '@/main/components/shared/copyToClipboardText/copyToClipboardText';
import ModalAllowSSOAlert from '../components/userSearchGrid/modalAllowSSO/modalAllowSSOAlert';
import { KOGAreaButtons } from '@/main/components/shared/styles';

const INITIAL_FORM_STATE = {
  country: null as KeyValue | null,
  companyType: null as KeyValue | null,
  company: null as KeyValue | null,
  companyBranch: null as KeyValue | null,
  name: '',
  email: '',
  login: '',
  phoneNr: '',
  cellphoneNr: '',
  userGroup: null as KeyValue | null,
  userLanguage: null as KeyValue | null,
  timeZone: null as KeyValue | null,
  status: null as KeyValue | null,
  locked: false,
  userType: null as KeyValue | null,
  allowSSO: false,
  secretSSO: ''
};
type IUserRegisterForm = typeof INITIAL_FORM_STATE;

function UserRegistrationScreen() {
  // hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, _] = useSearchParams();
  const guidUserParams = searchParams.get('userGuid');
  const {
    control,
    setValue,
    formState: { errors },
    getValues,
    clearErrors,
    watch,
    handleSubmit
  } = useForm<IUserRegisterForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });

  // Reducers
  const { currentUser: userReducer } = useUserReducer();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { companyType: companyTypeReducer } = useCompanyTypeReducer();
  const { company: companyReducer } = useCompanyReducer();
  const { companyBranch: companyBranchReducer } = useCompanyBranchReducer();
  const { usersGroup: userGroupReducer } = useUserGroupReducer();
  const { culture: cultureReducer } = useCultureReducer();
  const { timeZone: timeZoneReducer } = useTimeZoneReducer();
  const { userType: userTypeReducer } = useUserTypeReducer();

  // States
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isOpenModalAllowSSOAlert, setIsOpenModalAllowSSOAlert] =
    useState<boolean>(false);
  const [userGroupSelectedEdit, setUserGroupSelectedEdit] =
    useState<KeyValue | null>(null);
  const watcherCountry = watch('country');
  const watcherCompanyType = watch('companyType');
  const watcherLocked = watch('locked');
  const watcherUserType = watch('userType');
  const watcherAllowSSO = watch('allowSSO');

  // Methods & Variables for Form
  const typeCompanyUser = !userIsAdmin()
    ? String(userReducer?.idCompanyType)
    : null;
  const guidCompany =
    userReducer?.guidCompany !== null ? String(userReducer?.guidCompany) : null;
  const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.data);
  const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(
    companyTypeReducer.data,
    typeCompanyUser
  );
  const optionDefaultCompanyDrop = getDefaultOptionDrop(companyReducer.data);
  const optionDefaultCompanyBranchDrop = getDefaultOptionDrop(
    companyBranchReducer.data
  );
  const optionDefaultUserGroupDrop = getDefaultOptionDrop(
    userGroupReducer.data
  );
  const optionDefaultCultureDrop = getDefaultOptionDrop(cultureReducer.data);
  const optionDefaultTimeZoneDrop = getDefaultOptionDrop(timeZoneReducer.data);
  const optionDefaultUserTypeDrop = getDefaultOptionDrop(userTypeReducer.data);
  const isDisabledCountry = () => isEdit || optionDefaultCountryDrop !== null;

  const isDisabledCompanyDrop = () =>
    isEdit ||
    companyReducer.data === null ||
    getValues().companyType === null ||
    companyReducer.data.length <= 1;

  const isDisabledCompanyBranchDrop = () =>
    userReducer?.isCompanyBranch ||
    companyBranchReducer.data === null ||
    companyBranchReducer.data.length === 0 ||
    getValues().company === null ||
    getValues().companyType === null ||
    isEdit;

  const isDisabledCompanyType = () => {
    if (isEdit) return true;
    if (typeCompanyUser === String(CompanyTypeEnum.Insurer)) return false;
    return optionDefaultCompanyTypeDrop !== null;
  };

  const userIsInsurer = userReducer?.idCompanyType === CompanyTypeEnum.Insurer;

  const isLockedSelected =
    watcherLocked !== null && watcherLocked !== undefined && watcherLocked
      ? true
      : false;

  // Async functions
  function callCompanyApiDomain(companyTypeSelected: KeyValue | null) {
    const keyCompanyTypeSelected =
      companyTypeSelected !== null ? Number(companyTypeSelected.key) : null;

    const keyCountrySelected =
      watcherCountry !== null ? String(watcherCountry.key) : null;

    if (companyTypeSelected !== null && watcherCountry !== null) {
      // If the user is an insurer and the selected company is of another type, show only companies exclusively related to that insurer
      const seeOnlyCompaniesWithAnAgreement =
        userIsInsurer &&
        Number(companyTypeSelected.key) !== CompanyTypeEnum.Insurer;

      const seeOnlyExclusiveAgreement = seeOnlyCompaniesWithAnAgreement;

      const request: CompanyFilterRequest = {
        idCompanyType: keyCompanyTypeSelected,
        guidCountry: String(watcherCountry.key),
        idStatusRegister: StatusCompanyEnum.Active,
        type:
          Number(companyTypeSelected.key) === CompanyTypeEnum.BodyShop &&
          userReducer?.idCompanyType != CompanyTypeEnum.Insurer
            ? HeadQuarterBranchFilterEnum.Only_Headquarters
            : HeadQuarterBranchFilterEnum.All,
        seeOnlyCompaniesWithAnAgreement: seeOnlyCompaniesWithAnAgreement,
        seeOnlyExclusiveAgreement: seeOnlyExclusiveAgreement,
        guidCompany: !userIsAdmin() ? guidCompany : null
      };

      dispatch(getCompany(request));
    }

    loadGroups(keyCountrySelected, keyCompanyTypeSelected);

    setValue('company', null);
    setValue('companyBranch', null);
    setValue('userGroup', isEdit ? userGroupSelectedEdit : null);

    dispatch(resetReducerCompanyBranch());
    dispatch(resetReducerCompany());
    dispatch(resetReducerUsersGroupUser());

    clearErrors('company');
    clearErrors('companyBranch');
  }

  function callCompanyBranchApiDomain(companySelected: KeyValue | null) {
    const countrySelected = getValues().country;
    const companyTypeSelected = getValues().companyType;
    const keyCompanyTypeSelected =
      companyTypeSelected !== null ? Number(companyTypeSelected.key) : null;

    setValue('companyBranch', null);
    setValue('userGroup', isEdit ? userGroupSelectedEdit : null);
    dispatch(resetReducerCompanyBranch());
    dispatch(resetReducerUsersGroupUser());
    clearErrors('companyBranch');

    if (companySelected !== null) {
      const request: CompanyFilterRequest = {
        guidCompanyHeadQuarter: String(companySelected.key),
        guidCountry: String(countrySelected?.key),
        idCompanyType: Number(keyCompanyTypeSelected),
        idStatusRegister: StatusCompanyEnum.Active,
        type: HeadQuarterBranchFilterEnum.Only_Branches
      };
      dispatch(getCompanyBranch(request));
    }
    loadGroups(
      String(countrySelected?.key),
      keyCompanyTypeSelected,
      String(companySelected?.key)
    );
  }

  useEffect(() => {
    dispatch(getUserType());
    dispatch(
      getCompanyType({
        screenCompanytype: true
      })
    );
    dispatch(getCountriesUser(guidUserParams ? guidUserParams : null));
    setValue('status', {
      key: StatusUserEnum.Active,
      value: getTranslateByKey('statusUser.active')
    });
    setValue('userType', {
      key: UserTypeEnum.UI,
      value: getTranslateByKey('userType.ui')
    });
  }, []);

  useEffect(() => {
    // Call API's related by default country
    if (optionDefaultCountryDrop || watcherCountry) {
      const guidCountry = optionDefaultCountryDrop
        ? String(optionDefaultCountryDrop.key)
        : String(watcherCountry.key);
      dispatch(getCulture({ guidCountry }));
      dispatch(getTimeZonesUser({ guidCountry }));
      loadInfoCountry();
    }
  }, [optionDefaultCountryDrop, watcherCountry]);

  useEffect(() => {
    if (guidUserParams) {
      setIsEdit(true);
      setIsLoadingInfo(true);
      loadInfoUserRegistrationEdit(guidUserParams);
    }
  }, [guidUserParams]);

  useEffect(() => {
    // Call API's related by default country
    if (optionDefaultCountryDrop && optionDefaultCompanyTypeDrop) {
      callCompanyApiDomain(optionDefaultCompanyTypeDrop);
    }
  }, [optionDefaultCountryDrop, optionDefaultCompanyTypeDrop]);

  useEffect(() => {
    // Call API's related by default country and bodyshop
    if (optionDefaultCountryDrop && optionDefaultCompanyDrop) {
      callCompanyBranchApiDomain(optionDefaultCompanyDrop);
    }
  }, [optionDefaultCountryDrop, optionDefaultCompanyDrop]);

  const loadInfoUserRegistrationEdit = (guidUserParams: string) => {
    const request: UserDetailsRequest = {
      guidUser: guidUserParams,
      guidCompanyInsurer:
        userReducer?.idCompanyType == CompanyTypeEnum.Insurer
          ? userReducer.guidCompany
          : undefined
    };
    userApi
      .getUser(request)
      .then(response => {
        const resultData = response.data.result;
        setValue('country', {
          key: resultData.idCountryGuid,
          value: resultData.countryName
        });
        setValue('companyType', {
          key: resultData.idCompanyType,
          value: resultData.companyTypeName
        });
        setValue('userLanguage', {
          key: resultData.idUserLanguage,
          value: resultData.userLanguage
        });
        setValue('status', {
          key: resultData.idStatusRegister,
          value: resultData.statusName
        });
        setValue('name', resultData.name);
        setValue('email', resultData.email);
        setValue('login', resultData.login);
        setValue('phoneNr', resultData.phoneNr);
        setValue('cellphoneNr', resultData.cellphoneNr);
        setValue('locked', resultData.locked);
        setValue('timeZone', {
          key: resultData.idTimeZone,
          value: resultData.timeZone
        });
        setValue('allowSSO', resultData.allowSSO);
        setValue('secretSSO', resultData.secretSSO);

        callCompanyApiDomain({
          key: resultData.idCompanyType,
          value: resultData.companyTypeName
        });
        callCompanyBranchApiDomain({
          key: resultData.idCompanyGuid,
          value: resultData.companyName
        });
        setValue('company', {
          key: resultData.idCompanyGuid,
          value: resultData.companyName
        });
        setValue('companyBranch', {
          key: resultData.companyBranchGuid,
          value: resultData.companyBranchName
        });

        loadGroups(
          resultData.idCountryGuid,
          resultData.idCompanyType,
          resultData.idCompanyGuid
        );
        setUserGroupSelectedEdit({
          key: resultData.idUserGroup,
          value: resultData.userGroupName
        });
        setValue('userGroup', {
          key: resultData.idUserGroup,
          value: resultData.userGroupName
        });
        setValue('userType', {
          key: resultData.idUserType,
          value: resultData.userTypeName
        });
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingInfo(false);
      });
  };

  function loadGroups(
    countrySelected: string | null,
    companyTypeSelected: number | null,
    companySelected?: string | null
  ) {
    if (countrySelected === null || !companyTypeSelected) return;

    const groupFilter: UserGroupFilterRequest = {
      idCompanyType: companyTypeSelected,
      guidCompany: companySelected,
      guidCountry: String(countrySelected),
      active: true,
      includeGroupsWithoutCompany:
        userReducer?.idCompanyType == CompanyTypeEnum.Insurer ? false : true,
      idSystemApplication: null,
      seeOnlyCompaniesWithAnAgreement:
        userReducer?.idCompanyType == CompanyTypeEnum.Insurer ? true : false,
      guidCompanyInsurer:
        userReducer?.idCompanyType == CompanyTypeEnum.Insurer
          ? userReducer.guidCompany
          : undefined
    };

    dispatch(getUsersGroupUser(groupFilter));
  }

  function companyTypeSelectedIsBodyshopOrInsurer() {
    const companyTypeSelected = getValues('companyType')?.key;
    if (companyTypeSelected) {
      return Number(companyTypeSelected) === CompanyTypeEnum.BodyShop;
    }

    return false;
  }

  const userTypeIsUI =
    !isNullUndefined(watcherUserType) &&
    (Number(watcherUserType.key) === UserTypeEnum.UI ||
      Number(watcherUserType.key) === UserTypeEnum.SSOMaster);
  const isSSOAllowed = watcherAllowSSO;

  function openModalAllowSSOAlert() {
    if (!isSSOAllowed && isEdit) {
      setIsOpenModalAllowSSOAlert(true);
    }
  }

  function getRequestSave(data: IUserRegisterForm) {
    let request: UserSaveRequest = {
      guidCountry: String(data.country?.key),
      idCompanyType: Number(data.companyType?.key),
      guidCompany: String(data.company?.key),
      name: data.name,
      email: data.email,
      login: data.login,
      phoneNumber: data.phoneNr,
      cellphoneNr: data.cellphoneNr,
      guidUserGroup: String(data.userGroup?.key),
      guidUserLanguage: String(data.userLanguage?.key),
      guidTimeZone: String(data.timeZone?.key).toUpperCase(),
      active: Number(data.status?.key) === StatusUserEnum.Active,
      locked: data.locked,
      idUserType: Number(data.userType?.key),
      allowSSO: data.allowSSO,
      guidCompanyInsurer:
        userReducer?.idCompanyType == CompanyTypeEnum.Insurer
          ? userReducer.guidCompany
          : null,
      isSubDomainQualitas: isSubDomainQualitas()
    };

    if (
      request.idCompanyType === CompanyTypeEnum.BodyShop &&
      data.companyBranch !== null
    ) {
      request.guidCompanyBranch = String(data.companyBranch?.key);
    }

    if (guidUserParams) {
      request.idUser = guidUserParams;
    }

    return request;
  }

  const handleBack = () => {
    navigate(-1);
  };

  function handleSubmitSave(data: IUserRegisterForm) {
    setIsLoadingSave(true);
    userApi
      .saveUser(getRequestSave(data))
      .then(() => {
        const messageSuccess = isEdit
          ? getTranslateByKey('information.userEditSuccessfully')
          : getTranslateByKey('information.userEditSuccessfully');

        toast.success(messageSuccess);

        if (!isEdit) {
          handleBack();
        }
      })
      .finally(() => setIsLoadingSave(false));
  }

  function loadInfoCountry() {
    if (isNullUndefined(watcherCountry) || !isNullUndefined(guidUserParams))
      return;
    countryInfoApi.getCountryInfo(String(watcherCountry.key)).then(response => {
      const {
        data: { result }
      } = response;

      setValue('timeZone', {
        key: response.data.result.guidTimeZone,
        value: response.data.result.timeZoneName
      });

      setValue('userLanguage', {
        key: response.data.result.guidCulture,
        value: response.data.result.cultureName
      });
    });
  }

  if (isLoadingInfo) return <LoadingComponent />;

  return (
    <S.Container>
      <S.HeaderText>
        {getTranslateByKey('information.registration')}
        {' > '}
        {getTranslateByKey('information.registerUser')}
      </S.HeaderText>
      <Box title={getTranslateByKey('information.filters')}>
        <S.RowInputs>
          <KogAutoCompleteControlled
            required
            label={getTranslateByKey('information.country')}
            control={control}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            nameCustom="country"
            rulesCustom={{ required: true }}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              if (optionSelected !== null) {
                const guidCountry = String(optionSelected.key);
                dispatch(getCulture({ guidCountry }));
                dispatch(getTimeZonesUser({ guidCountry }));
                callCompanyApiDomain(watcherCompanyType);
              }

              setValue('company', null);
              setValue('companyBranch', null);
              setValue('userGroup', null);
              dispatch(resetReducerCompanyBranch());
              dispatch(resetReducerCompany());
              dispatch(resetReducerUsersGroupUser());
            }}
            autoHighlightCustom={!!optionDefaultCountryDrop}
            setValueCustom={setValue}
            disabled={isDisabledCountry()}
            id="id-registerUser-select-country"
          />
          <KogAutoCompleteControlled
            required
            label={getTranslateByKey('information.companyType')}
            control={control}
            options={companyTypeReducer.data}
            isLoading={companyTypeReducer.isLoading}
            nameCustom="companyType"
            rulesCustom={{ required: true }}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callCompanyApiDomain(optionSelected);
            }}
            valueDefaultKey={
              isEdit ? undefined : optionDefaultCompanyTypeDrop?.key
            }
            setValueCustom={setValue}
            disabled={isDisabledCompanyType()}
            id="id-registerUser-select-companyType"
          />
          <KogAutoCompleteControlled
            required
            isDefaultValueUnique
            label={getTranslateByKey('information.company')}
            control={control}
            options={companyReducer.data}
            isLoading={companyReducer.isLoading}
            nameCustom="company"
            rulesCustom={{ required: true }}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callCompanyBranchApiDomain(optionSelected);
            }}
            valueDefaultKey={isEdit ? undefined : optionDefaultCompanyDrop?.key}
            setValueCustom={setValue}
            disabled={isDisabledCompanyDrop()}
            id="id-registerUser-select-company"
          />
          {companyTypeSelectedIsBodyshopOrInsurer() && (
            <KogAutoCompleteControlled
              required
              nameCustom="companyBranch"
              label={getTranslateByKey('information.companyBranch')}
              control={control}
              options={companyBranchReducer.data}
              isLoading={companyBranchReducer.isLoading}
              rulesCustom={{ required: false }}
              onChangeCustom={() => {}}
              valueDefaultKey={
                isEdit ? undefined : optionDefaultCompanyBranchDrop?.key
              }
              disabled={isDisabledCompanyBranchDrop()}
              id="id-registerUser-select-companyBranch"
              setValueCustom={setValue}
            />
          )}
          <KogInputTextControlledNew
            name="name"
            label={getTranslateByKey('information.name')}
            control={control}
            inputProps={{ maxLength: 100 }}
            controllerProps={{ rules: { required: true } }}
            error={!!errors.name}
            id="id-registerUser-txt-name"
          />
          <KogInputTextControlledNew
            name="email"
            label={getTranslateByKey('information.email')}
            control={control}
            inputProps={{ maxLength: 100 }}
            controllerProps={{ rules: { required: true } }}
            error={!!errors.email}
            id="id-registerUser-txt-email"
          />
          <KogInputTextControlledNew
            name="login"
            label={getTranslateByKey('information.login')}
            control={control}
            inputProps={{ maxLength: 50 }}
            controllerProps={{ rules: { required: true } }}
            error={!!errors.login}
            disabled={isEdit}
            id="id-registerUser-txt-login"
          />
          <KogInputTextControlledNew
            name="phoneNr"
            label={getTranslateByKey('information.phoneNr')}
            inputProps={{ maxLength: 20 }}
            control={control}
            controllerProps={{ rules: { required: true } }}
            error={!!errors.phoneNr}
            id="id-registerUser-txt-phoneNr"
          />
          <KogInputTextControlledNew
            name="cellphoneNr"
            label={getTranslateByKey('information.cellphoneNr')}
            error={false}
            inputProps={{ maxLength: 20 }}
            control={control}
            id="id-registerUser-txt-cellphoneNr"
          />
          <KogAutoCompleteControlled
            required
            nameCustom="userGroup"
            label={getTranslateByKey('information.userGroup')}
            control={control}
            options={userGroupReducer.data}
            isLoading={userGroupReducer.isLoading}
            rulesCustom={{ required: true }}
            onChangeCustom={() => {}}
            autoHighlightCustom={!!optionDefaultUserGroupDrop}
            id="id-registerUser-select-userGroup"
          />
          <KogAutoCompleteControlled
            required
            nameCustom="userLanguage"
            label={getTranslateByKey('information.userLanguage')}
            control={control}
            options={cultureReducer.data}
            isLoading={cultureReducer.isLoading}
            rulesCustom={{ required: true }}
            onChangeCustom={() => {}}
            autoHighlightCustom={!!optionDefaultCultureDrop}
            id="id-registerUser-select-userLanguage"
          />
          <KogAutoCompleteControlled
            nameCustom="timeZone"
            label={getTranslateByKey('information.timezone')}
            control={control}
            options={timeZoneReducer.data}
            isLoading={timeZoneReducer.isLoading}
            rulesCustom={{ required: true }}
            onChangeCustom={() => {}}
            autoHighlightCustom={!!optionDefaultTimeZoneDrop}
            id="id-registerUser-select-timeZone"
          />
          <KogAutoCompleteControlled
            required
            nameCustom="userType"
            label={getTranslateByKey('information.usertypetxt')}
            control={control}
            options={userTypeReducer.data}
            isLoading={userTypeReducer.isLoading}
            rulesCustom={{ required: true }}
            onChangeCustom={() => {}}
            autoHighlightCustom={!!optionDefaultUserTypeDrop}
            id="id-registerUser-select-usertype"
          />
          <KogAutoCompleteControlled
            required
            nameCustom="status"
            label={getTranslateByKey('information.status')}
            control={control}
            options={getOptionsStatus()}
            isLoading={false}
            rulesCustom={{ required: true }}
            onChangeCustom={() => {}}
            disabled={isLockedSelected}
            id="id-registerUser-select-status"
          />
          <KogCheckBoxControlled
            nameCustom="locked"
            label={`${getTranslateByKey('information.locked')} ?`}
            control={control}
            id="id-registerUser-checkbox-locked"
          />
          {userTypeIsUI && isEdit && (
            <KogCheckBoxControlled
              nameCustom="allowSSO"
              label={getTranslateByKey('information.allowSSO')}
              control={control}
              id="id-registerUser-checkbox-allowSSO"
              onChangeCustom={() => {
                openModalAllowSSOAlert();
              }}
            />
          )}
          {userTypeIsUI && isSSOAllowed && isEdit && (
            <CopyToClipboardText
              label={getTranslateByKey('information.SSOSecret')}
              valueToCopy={getValues('secretSSO')}>
              {
                <KogInputTextControlledNew
                  name="secretSSO"
                  label={getTranslateByKey('information.SSOSecret')}
                  control={control}
                  disabled={true}
                  id="id-registerUser-txt-secretSSO"
                />
              }
            </CopyToClipboardText>
          )}
        </S.RowInputs>
        <KOGAreaButtons $isRegisterScreen>
          <SecondaryButton onClick={handleBack} id="id-registerUser-btn-back">
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <Button
            id="id-registerUser-btn-save"
            type="submit"
            isLoading={isLoadingSave}
            onClick={handleSubmit(handleSubmitSave)}>
            {getTranslateByKey('information.save')}
          </Button>
        </KOGAreaButtons>
        <ModalAllowSSOAlert
          isOpenModal={isOpenModalAllowSSOAlert}
          setIsOpenModal={setIsOpenModalAllowSSOAlert}
          closeModal={() => setIsOpenModalAllowSSOAlert(false)}
        />
      </Box>
    </S.Container>
  );
}

export default UserRegistrationScreen;
